import React, { useEffect, useState } from 'react';
import DataTable from "react-data-table-component";
import {
    Button,
    FormGroup,
    Input,
    Label,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "reactstrap";

import { useSelector } from "react-redux";
import '../../assets/scss/ReactBootstrapTable.scss';
import moment from "moment";
import * as Icon from 'react-feather';
import ComponentCard from "../ComponentCard";
import useHttp from "../../hooks/useHttp";
import BreadCrumbs from '../../layouts/breadcrumbs/BreadCrumbs';
import customStyles from "../TableStyles";
import useAlert from "../../hooks/useAlert";
import FilterComponent from "../utils/Filter";
import useExport from "../../hooks/useExport";

const BroadbandTransactions = () => {

    const [apiData, setApiData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);
    const [subitemsData, setSubitemsData] = useState([]);
    const [breakupData,  setBreakupData] = useState([]);
    const [isSubitemModalOpen, setIsSubitemModalOpen] = useState(false); // State to control modal visibility
    const [isBreakupModalOpen, setIsBreakupModalOpen] = useState(false); // State to control modal visibility
    const isDarkMode = useSelector((state) => state.customizer.isDark);
    const { isLoading, error, sendRequest } = useHttp();
    const { alert, errorAlert } = useAlert();
    const { exportToExcel } = useExport();
    const [searchTransactionType, setSearchTransactionType] = useState('ALL');
    const [searchStartDate, setSearchStartDate] = useState(moment(new Date().setDate(new Date().getDate() - 1)).format("YYYY-MM-DD"));
    const [searchEndDate, setSearchEndDate] = useState(moment(new Date()).format("YYYY-MM-DD"));
    const [filterText, setFilterText] = useState('');
    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState('');

    const broadbandTransactions = (forExport) => {
        const URL = `${process.env.REACT_APP_IBILL_GATEWAY}/broadband/api/v1/admin/transactions`
          + `?fromDate=${searchStartDate ? moment(searchStartDate).format('DD-MM-YYYY') : ''}`
          + `&toDate=${searchEndDate ? moment(searchEndDate).format('DD-MM-YYYY') : ''}`
          + `&status=${searchTransactionType || ''}`
          + `&forExport=${forExport}`;
        return {
            method: 'GET',
            url: URL,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        };
    };

    const handleBreakupClick = async (transactionUid) => {
        setSelectedTransactionId(transactionUid);
        await sendRequest({
            method: 'GET',
            url: `${process.env.REACT_APP_IBILL_GATEWAY}/broadband/api/v1/admin/transactions/${transactionUid}/breakups`,
            headers: { 'Accept': 'application/json' }
        }, (response) => {
            setBreakupData(response || []);
            setIsBreakupModalOpen(true); // Open the modal after data is fetched
        });
    };

    const handleSubitemsClick = async (transactionUid) => {
        setSelectedTransactionId(transactionUid);
        await sendRequest({
            method: 'GET',
            url: `${process.env.REACT_APP_IBILL_GATEWAY}/broadband/api/v1/admin/transactions/${transactionUid}/subitems`,
            headers: { 'Accept': 'application/json' }
        }, (response) => {
            setSubitemsData(response || []);
            setIsSubitemModalOpen(true); // Open the modal after data is fetched
        });
    };

    const toggleSubitemsModal = () => {
        setIsSubitemModalOpen(!isSubitemModalOpen); // Toggle modal visibility
    };

    const toggleBreakupsModal = () => {
        setIsBreakupModalOpen(!isBreakupModalOpen); // Toggle modal visibility
    };

    const search = async (forExport) => {
        if (!searchStartDate || searchStartDate === '') {
            errorAlert("Start Date is empty");
            return;
        }
        if (!searchEndDate || searchEndDate === '') {
            errorAlert("End Date is empty");
            return;
        }

        const apiCall = broadbandTransactions(forExport);

        await sendRequest(apiCall, (response) => {
            const transactions = response || [];
            setApiData(transactions);
        });
    };

    useEffect(() => {
        search(false);
    }, [sortDirection, sortColumn]);


    useEffect(() => error ? errorAlert(error) : null, [error]);

    const handleSort = (column, order) => {
        setSortColumn(column.sortId);
        setSortDirection(order);
    };

    const columns = [
        {
            name: 'Transaction Date',
            sortId: 'transactionDate',
            selector: row => row.transactionDate,
            sortable: true,
            width: '175px',
            wrap: false,
            reorder: true,
        },
        {
            name: 'Order ID',
            sortId: 'transactionUid',
            selector: row => row.transactionUid,
            sortable: false,
            width: '200px',
            wrap: false,
            reorder: true,
        },
        {
            name: 'Status',
            sortId: 'status',
            selector: row => row.status,
            sortable: true,
            width: '125px',
            wrap: false,
            reorder: true,
        },
        {
            name: 'Actions',
            button: true,
            width: '120px',
            cell: (row) => {
                return (
                  <>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm m-1"
                        onClick={() => handleBreakupClick(row.transactionUid)} >
                          <Icon.Airplay size={20} />
                      </button>

                      <button
                        type="button"
                        className="btn btn-warning btn-sm m-1"
                        onClick={() => handleSubitemsClick(row.transactionUid)} >
                          <Icon.Eye size={20} />
                      </button>
                  </>
                );
            }
        },
        {
            name: 'Customer ID',
            sortId: 'customerId',
            selector: row => row.customerId,
            sortable: true,
            width: '150px',
            wrap: false,
            reorder: true,
        },
        {
            name: 'Customer Name',
            sortId: 'customerName',
            selector: row => row.customerName,
            sortable: true,
            width: '200px',
            wrap: true,
            reorder: true,
        },
        {
            name: 'Amount',
            sortId: 'amount',
            selector: row => row.amount?.toFixed(2),
            sortable: true,
            width: '125',
            wrap: false,
            reorder: true,
        },
        {
            name: 'Payment Type',
            sortId: 'paymentType',
            selector: row => row.paymentType,
            sortable: true,
            width: '150px',
            wrap: true,
            reorder: true,
        },
        {
            name: 'Platform',
            sortId: 'device',
            selector: row => row.device,
            sortable: true,
            width: '125px',
            wrap: true,
            reorder: true,
        },
        {
            name: 'Zone',
            sortId: 'zoneCode',
            selector: row => row.zoneCode,
            sortable: true,
            width: '350px',
            wrap: true,
            reorder: true,
        },
    ];

    const headers = ["Transaction Date", "Order ID",  "Status", "Customer ID", "Customer Name", "Amount", "Payment Type", "Platform", "Zone"];

    const getOnExport = async () => {
        const apiCall = broadbandTransactions(true);

        await sendRequest(apiCall, (data) => {
            const exportData = data || [];
            const excelRows = exportData.map(row => ({
                transactionDate: row.transactionDate || '',
                transactionUid: row.transactionUid || '',
                status: row.status || '',
                customerId: row.customerId || '',
                customerName: row.customerName || '',
                amount: row.amount ? row.amount.toFixed(2) : '',
                paymentType: row.paymentType || '',
                device: row.device || '',
                zoneCode: row.zoneCode || ''
            }));
            exportToExcel(excelRows, headers, "Broadband_Recharges", "Broadband_Recharges");
        });
    };

    useEffect(() => {
        // Filter data locally based on filterText
        const filtered = apiData.filter(item =>
          Object.values(item).some(value =>
            value?.toString().toLowerCase().includes(filterText.toLowerCase())
          )
        );
        setFilteredData(filtered);
    }, [filterText, apiData]);

    const subHeaderComponent = <FilterComponent onFilter={e => setFilterText(e.target.value)} filterText={filterText} onExport={() => getOnExport()} />;

    const transactionTypeChangeHandler = (e) => { setSearchTransactionType(e.target.value); search(false); };
    const startDateChangeHandler = (e) => { setSearchStartDate(e.target.value); search(false); };
    const endDateChangeHandler = (e) => { setSearchEndDate(e.target.value); search(false); };

    return (
      <>
          {alert}
          <BreadCrumbs />
          <ComponentCard title="Online Payment Transactions">
              <Row>
                  <FormGroup className="col-sm-2 col-xs-12">
                      <Label>Date Range Start </Label>
                      <Input
                        type="date"
                        value={searchStartDate}
                        onChange={startDateChangeHandler}
                        required="required"
                      />
                  </FormGroup>
                  <FormGroup className="col-sm-2 col-xs-12">
                      <Label>Date Range End </Label>
                      <Input
                        type="date"
                        value={searchEndDate}
                        onChange={endDateChangeHandler}
                        required="required"
                      />
                  </FormGroup>
                  <FormGroup className="col-sm-2 col-xs-12">
                      <Label>Transaction Type</Label>
                      <Input
                        type="select"
                        value={searchTransactionType}
                        onChange={transactionTypeChangeHandler}
                      >
                          <option value="ALL">ALL</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="FAILED">Failed</option>
                          <option value="IN_PROGRESS">In Progress</option>
                      </Input>
                  </FormGroup>
                  <FormGroup className="col-sm-2 col-xs-12">
                      <Label>&nbsp;</Label>
                      <Button className="btn col-12 btn-block btn-outline-warning" onClick={() => search(false)}>Search</Button>
                  </FormGroup>
              </Row>
              <hr />
              <DataTable
                columns={columns}
                data={filteredData}
                striped
                dense
                fixedHeader
                fixedHeaderScrollHeight='500px'
                theme={isDarkMode ? 'iDark' : 'iLight'}
                customStyles={customStyles}
                subHeader
                subHeaderComponent={subHeaderComponent}
                progressPending={isLoading}
                sortServer
                onSort={handleSort}
              />

              {/* Modal for displaying subitems */}
              <Modal isOpen={isSubitemModalOpen} toggle={toggleSubitemsModal} size="lg">
                  <ModalHeader toggle={toggleSubitemsModal}>
                      Details of Transaction ID: <strong style={{ color: 'orange' }}> {selectedTransactionId} </strong>
                  </ModalHeader>
                  <ModalBody>
                      <DataTable
                        columns={[
                            { name: 'Created On', selector: row => row.createdOn, sortable: true, width: '25%' },
                            { name: 'Request Type', selector: row => row.requestType, sortable: false, width: '50%' },
                            { name: 'Status', selector: row => row.status, sortable: false, width: '25%' },
                        ]}
                        data={subitemsData}
                        theme={isDarkMode ? 'iDark' : 'iLight'}
                        striped
                        dense
                        fixedHeader
                        fixedHeaderScrollHeight="300px"
                      />
                  </ModalBody>
                  <ModalFooter>
                      <button type="button" className="btn btn-secondary" onClick={toggleSubitemsModal}>
                          Close
                      </button>
                  </ModalFooter>
              </Modal>

              {/* Modal for displaying transaction breakups */}
              <Modal isOpen={isBreakupModalOpen} toggle={toggleBreakupsModal} size="xl">
                  <ModalHeader toggle={toggleBreakupsModal}>
                      Details of Transaction ID: <strong style={{ color: 'orange' }}> {selectedTransactionId} </strong>
                  </ModalHeader>
                  <ModalBody>
                      <DataTable
                        columns={[
                            { name: 'Order ID', selector: row => row.orderId, sortable: true, width: '25%' },
                            { name: 'Service Name', selector: row => row.serviceName, sortable: false, width: '20%' },
                            { name: 'Items', selector: row => row.serviceItemNames, sortable: false, width: '25%' },
                            { name: 'Amount', selector: row => row.amount, sortable: false, width: '15%' },
                            { name: 'Status', selector: row => row.status, sortable: false, width: '15%' },
                        ]}
                        data={breakupData}
                        theme={isDarkMode ? 'iDark' : 'iLight'}
                        striped
                        dense
                        fixedHeader
                        fixedHeaderScrollHeight="300px"
                      />
                  </ModalBody>
                  <ModalFooter>
                      <button type="button" className="btn btn-secondary" onClick={toggleBreakupsModal}>
                          Close
                      </button>
                  </ModalFooter>
              </Modal>
          </ComponentCard>
      </>
    );
};

export default BroadbandTransactions;